/**
 * Fixture data for the landing page hero.
 *
 * These are shaped to match the real `GET /api/prs` payload exactly, so the
 * hero renders the actual PrCard / StatCard components rather than a facsimile
 * that could drift from the product. Verified against a live row on
 * 2026-08-11 — in particular:
 *
 *   - `complexity_score` is a **string**, not a number. Postgres returns
 *     numeric/decimal as text through the pg driver, and PrCard parseFloat()s
 *     it. Using numbers here would render identically but stop being a
 *     faithful fixture, hiding that class of bug from anyone testing against
 *     this data.
 *   - `tags` is an array of strings matching PrCard's tagColors keys.
 *   - `github_created_at` is an ISO string.
 *
 * Timestamps are relative to now so the "2h ago" labels never go stale.
 */

const hoursAgo = (h) => new Date(Date.now() - h * 3600000).toISOString();

export const heroPrs = [
  {
    pr_id: "acme/payments-api#412",
    repository: "acme/payments-api",
    pr_number: 412,
    title: "Rotate Stripe webhook signing keys on schedule",
    author_github_username: "priya-n",
    state: "open",
    priority: "critical",
    lines_added: 486,
    lines_deleted: 132,
    files_changed: 14,
    complexity_score: "8.4",
    estimated_review_time: 95,
    tags: ["auth", "backend", "security"],
    assigned_reviewer: "dmitri-k",
    github_created_at: hoursAgo(2),
  },
  {
    pr_id: "acme/payments-api#409",
    repository: "acme/payments-api",
    pr_number: 409,
    title: "Add idempotency keys to refund endpoint",
    author_github_username: "sam-oyelaran",
    state: "in_review",
    priority: "high",
    lines_added: 214,
    lines_deleted: 41,
    files_changed: 8,
    complexity_score: "6.1",
    estimated_review_time: 45,
    tags: ["backend", "database"],
    assigned_reviewer: "priya-n",
    github_created_at: hoursAgo(6),
  },
  {
    pr_id: "acme/dashboard#188",
    repository: "acme/dashboard",
    pr_number: 188,
    title: "Virtualise the transactions table",
    author_github_username: "dmitri-k",
    state: "in_review",
    priority: "medium",
    lines_added: 97,
    lines_deleted: 63,
    files_changed: 4,
    complexity_score: "3.8",
    estimated_review_time: 20,
    tags: ["frontend"],
    assigned_reviewer: "sam-oyelaran",
    github_created_at: hoursAgo(9),
  },
  {
    pr_id: "acme/dashboard#187",
    repository: "acme/dashboard",
    pr_number: 187,
    title: "Bump eslint-config to v9",
    author_github_username: "priya-n",
    state: "open",
    priority: "low",
    lines_added: 11,
    lines_deleted: 9,
    files_changed: 2,
    complexity_score: "0.9",
    estimated_review_time: 5,
    tags: ["dependencies", "config"],
    assigned_reviewer: null,
    github_created_at: hoursAgo(26),
  },
];

/**
 * Reviewer candidates for the history-aware routing spotlight, shaped to
 * match findBestReviewer()'s real output (lambda/shared/routing-engine.mjs)
 * — file_expertise (SUM of user_file_expertise.times_modified for this PR's
 * files) and queue_depth (pending review count). The winner's caption uses
 * the exact "file expertise score: N, queue depth: M" format
 * assignReviewer() writes to review_assignments.assignment_reason, not a
 * paraphrase.
 */
export const routingCandidates = [
  { name: "Dmitri K.", username: "dmitri-k", fileExpertise: 14, queueDepth: 1, winner: true },
  { name: "Priya N.", username: "priya-n", fileExpertise: 3, queueDepth: 0, winner: false },
  { name: "Sam O.", username: "sam-oyelaran", fileExpertise: 0, queueDepth: 2, winner: false },
];

const daysAgo = (d) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - d);
  return date.toISOString().slice(0, 10);
};

/**
 * Shaped to match /api/analytics's trend array exactly (get-analytics's
 * densified per-day rows), for the team analytics spotlight's TrendLineChart.
 * A modest, realistic gap — opened mildly outpacing resolved — not a
 * dramatic one; overstating the bottleneck here would be the same mistake
 * the comparison table was cut to avoid.
 */
export const analyticsTrend = [
  { date: daysAgo(13), prs_opened: 3, prs_resolved: 3 },
  { date: daysAgo(12), prs_opened: 4, prs_resolved: 3 },
  { date: daysAgo(11), prs_opened: 2, prs_resolved: 4 },
  { date: daysAgo(10), prs_opened: 5, prs_resolved: 3 },
  { date: daysAgo(9), prs_opened: 3, prs_resolved: 3 },
  { date: daysAgo(8), prs_opened: 4, prs_resolved: 2 },
  { date: daysAgo(7), prs_opened: 6, prs_resolved: 4 },
  { date: daysAgo(6), prs_opened: 3, prs_resolved: 3 },
  { date: daysAgo(5), prs_opened: 5, prs_resolved: 3 },
  { date: daysAgo(4), prs_opened: 4, prs_resolved: 2 },
  { date: daysAgo(3), prs_opened: 6, prs_resolved: 3 },
  { date: daysAgo(2), prs_opened: 5, prs_resolved: 4 },
  { date: daysAgo(1), prs_opened: 4, prs_resolved: 3 },
  { date: daysAgo(0), prs_opened: 5, prs_resolved: 2 },
];

/**
 * Derived exactly the way Dashboard.js derives them, including the
 * parseFloat() on complexity_score — so the numbers shown in the hero are the
 * numbers the real dashboard would compute from this data.
 */
export const heroStats = {
  // "Pending Reviews" was renamed to "Awaiting Changes" (item 21) — matches
  // Dashboard.js's own stats.pending, which now reads state === "awaiting_changes".
  pending: heroPrs.filter((p) => p.state === "awaiting_changes").length,
  inReview: heroPrs.filter((p) => p.state === "in_review").length,
  avgTime: Math.round(
    heroPrs.reduce((a, p) => a + (p.estimated_review_time || 0), 0) / heroPrs.length
  ),
  avgComplexity: (
    heroPrs.reduce((a, p) => a + (parseFloat(p.complexity_score) || 0), 0) / heroPrs.length
  ).toFixed(1),
};
